/**
 * Every sound in Dimples, synthesized. There are no audio files and never will
 * be: each voice below is oscillators, one shared noise buffer and envelopes.
 *
 * The mix has exactly one rule. The contact crack is the loudest thing in the
 * game and everything else sits about 12 dB under it, so `setDuck(1)` during
 * the downswing pulls ambience and wind to near-silence and the crack lands on
 * quiet. Comedy is contrast, and contrast is mostly silence placed correctly.
 *
 * Audio runs on the *unscaled* clock. Slow-mo must never pitch-bend a tick:
 * the three-beat telegraph is the game's fairness guarantee on a phone, where
 * audio timing is far steadier than requestAnimationFrame.
 */

export type SfxName =
	| 'tick'
	| 'tickFinal'
	| 'whoosh'
	| 'squeak'
	| 'grunt'
	| 'squat'
	| 'hop'
	| 'crackFlush'
	| 'crackGood'
	| 'clank'
	| 'donk'
	| 'whiffAir'
	| 'bounceTurf'
	| 'bounceHard'
	| 'splash'
	| 'skim'
	| 'plug'
	| 'tantrum'
	| 'stormOff'
	| 'plonk'
	| 'piano'
	| 'rest'
	| 'sunset'
	| 'uiStamp'

/**
 * The whole mix, one row per voice. These belong in tuning.ts once the levels
 * settle; they live here for now so tuning.ts stays the gameplay file.
 *   gain — linear peak, graded against crackFlush at 1.0 (0.25 is ~12 dB under)
 *   dur  — worst-case tail in seconds, used only to reclaim the voice's nodes
 *   vary — random +/- playbackRate spread. Zero for the ticks: the telegraph
 *          must sound identical every time or it stops being a promise.
 */
export const MIX: Record<SfxName, { gain: number; dur: number; vary: number }> = {
	tick: { gain: 0.34, dur: 0.1, vary: 0 },
	tickFinal: { gain: 0.44, dur: 0.18, vary: 0 },
	whoosh: { gain: 0.26, dur: 0.45, vary: 0.15 },
	squeak: { gain: 0.22, dur: 0.26, vary: 0.15 },
	grunt: { gain: 0.28, dur: 0.42, vary: 0.15 },
	squat: { gain: 0.18, dur: 0.26, vary: 0.15 },
	hop: { gain: 0.2, dur: 0.22, vary: 0.15 },
	crackFlush: { gain: 1.0, dur: 0.3, vary: 0.06 },
	crackGood: { gain: 0.82, dur: 0.28, vary: 0.08 },
	clank: { gain: 0.5, dur: 0.32, vary: 0.12 },
	donk: { gain: 0.95, dur: 0.5, vary: 0.15 },
	whiffAir: { gain: 0.2, dur: 0.4, vary: 0.15 },
	bounceTurf: { gain: 0.3, dur: 0.22, vary: 0.15 },
	bounceHard: { gain: 0.36, dur: 0.18, vary: 0.15 },
	splash: { gain: 0.4, dur: 0.75, vary: 0.12 },
	skim: { gain: 0.38, dur: 0.2, vary: 0.15 },
	plug: { gain: 0.26, dur: 0.22, vary: 0.15 },
	tantrum: { gain: 0.4, dur: 0.85, vary: 0.1 },
	stormOff: { gain: 0.3, dur: 1.0, vary: 0.05 },
	plonk: { gain: 0.42, dur: 1.35, vary: 0.04 },
	// The one note the game ever plays on purpose. No variance: it has to
	// land the same way every time it is earned.
	piano: { gain: 0.5, dur: 4.2, vary: 0 },
	rest: { gain: 0.14, dur: 0.16, vary: 0.15 },
	sunset: { gain: 0.3, dur: 3.5, vary: 0.02 },
	uiStamp: { gain: 0.28, dur: 0.22, vary: 0.15 },
}

/** Master chain levels. These belong in tuning.ts later too. */
export const MASTER_GAIN = 0.62
/** Where ambience and wind land at full duck. Not zero — a hole would read as a bug. */
export const DUCK_FLOOR = 0.05
export const DUCK_TAU = 0.03
export const AMBIENCE_GAIN = 0.5
export const WIND_MAX_GAIN = 0.17
/** Seconds of white noise held in the one shared buffer. */
export const NOISE_SECONDS = 2.5

type PlayOpts = { rate?: number; gain?: number; pan?: number }

const clamp = (x: number, lo: number, hi: number) => (x < lo ? lo : x > hi ? hi : x)

/** Soft clip for the shank. Grit is what makes a bad strike sound wrong. */
let clipCurve: Float32Array<ArrayBuffer> | null = null
function softClip(): Float32Array<ArrayBuffer> {
	if (!clipCurve) {
		const n = 1024
		const c = new Float32Array(n)
		for (let i = 0; i < n; i++) {
			c[i] = Math.tanh(((i / (n - 1)) * 2 - 1) * 2.6)
		}
		clipCurve = c
	}
	return clipCurve
}

export class Sfx {
	private ctx: AudioContext | null = null
	private sfxBus: GainNode | null = null
	/** Ambience and wind hang off this so one node ducks both. */
	private duckBus: GainNode | null = null
	private ambBus: GainNode | null = null
	private noiseBuf: AudioBuffer | null = null

	private windGain: GainNode | null = null
	private windLp: BiquadFilterNode | null = null
	private windTarget = 0
	private duckTarget = 0

	private ambOn = false
	private ambWanted = false
	private ambSources: OscillatorNode[] = []
	private ambChain: AudioNode[] = []
	private birdTimer: number | null = null
	private thwackTimer: number | null = null

	get ready(): boolean {
		return this.ctx !== null && this.ctx.state === 'running'
	}

	/** Call from the first pointerdown. iOS hands out audio only inside a gesture. */
	unlock(): void {
		if (!this.ctx) {
			const w = window as unknown as { webkitAudioContext?: typeof AudioContext }
			const Ctor = window.AudioContext ?? w.webkitAudioContext
			if (!Ctor) return
			try {
				this.ctx = new Ctor()
			} catch {
				this.ctx = null
				return
			}
			this.build()
		}
		const ctx = this.ctx
		if (!ctx) return
		if (ctx.state === 'suspended') void ctx.resume().catch(() => {})
		if (this.ambWanted && !this.ambOn) this.startAmbience()
	}

	private build(): void {
		const ctx = this.ctx
		if (!ctx) return

		const buf = ctx.createBuffer(1, Math.floor(ctx.sampleRate * NOISE_SECONDS), ctx.sampleRate)
		const data = buf.getChannelData(0)
		for (let i = 0; i < data.length; i++) {
			data[i] = Math.random() * 2 - 1
		}
		this.noiseBuf = buf

		// A gentle limiter so a flush crack can be genuinely loud without clipping.
		const limiter = ctx.createDynamicsCompressor()
		limiter.threshold.value = -6
		limiter.knee.value = 0
		limiter.ratio.value = 20
		limiter.attack.value = 0.002
		limiter.release.value = 0.16

		const master = ctx.createGain()
		master.gain.value = MASTER_GAIN
		const image = ctx.createStereoPanner?.() ?? null
		if (image) {
			master.connect(image)
			image.connect(limiter)
		} else {
			master.connect(limiter)
		}
		limiter.connect(ctx.destination)

		const sfx = ctx.createGain()
		sfx.gain.value = 1
		sfx.connect(master)
		this.sfxBus = sfx

		const duck = ctx.createGain()
		duck.gain.value = 1 - this.duckTarget * (1 - DUCK_FLOOR)
		duck.connect(master)
		this.duckBus = duck

		const amb = ctx.createGain()
		amb.gain.value = AMBIENCE_GAIN
		amb.connect(duck)
		this.ambBus = amb
	}

	// ── primitives ───────────────────────────────────────────────────────────

	private makePanner(pan: number): AudioNode {
		const ctx = this.ctx as AudioContext
		if (typeof ctx.createStereoPanner === 'function') {
			const p = ctx.createStereoPanner()
			p.pan.value = clamp(pan, -1, 1)
			return p
		}
		return ctx.createGain()
	}

	/** Attack-decay envelope. Everything in this file is one of these. */
	private env(p: AudioParam, t: number, peak: number, atk: number, dec: number): void {
		const a = Math.max(atk, 0.0005)
		const d = Math.max(dec, 0.005)
		const v = Math.max(peak, 0.0001)
		p.setValueAtTime(0, t)
		p.linearRampToValueAtTime(v, t + a)
		p.exponentialRampToValueAtTime(v * 0.0006, t + a + d)
		p.setValueAtTime(0, t + a + d)
	}

	private tone(
		type: OscillatorType,
		t: number,
		f0: number,
		f1: number,
		atk: number,
		dec: number,
		peak: number,
		dest: AudioNode,
	): void {
		const ctx = this.ctx as AudioContext
		const total = Math.max(atk, 0.0005) + Math.max(dec, 0.005)
		const o = ctx.createOscillator()
		o.type = type
		o.frequency.setValueAtTime(Math.max(f0, 8), t)
		if (Math.abs(f1 - f0) > 0.5) {
			o.frequency.exponentialRampToValueAtTime(Math.max(f1, 8), t + total)
		}
		const g = ctx.createGain()
		this.env(g.gain, t, peak, atk, dec)
		o.connect(g)
		g.connect(dest)
		o.start(t)
		o.stop(t + total + 0.02)
	}

	private noise(
		t: number,
		type: BiquadFilterType,
		f0: number,
		f1: number,
		q: number,
		atk: number,
		dec: number,
		peak: number,
		dest: AudioNode,
	): void {
		const ctx = this.ctx as AudioContext
		const buf = this.noiseBuf
		if (!buf) return
		const total = Math.max(atk, 0.0005) + Math.max(dec, 0.005)
		const f = ctx.createBiquadFilter()
		f.type = type
		f.Q.value = q
		f.frequency.setValueAtTime(clamp(f0, 20, 20000), t)
		if (Math.abs(f1 - f0) > 1) {
			f.frequency.exponentialRampToValueAtTime(clamp(f1, 20, 20000), t + total)
		}
		const g = ctx.createGain()
		this.env(g.gain, t, peak, atk, dec)
		const src = ctx.createBufferSource()
		src.buffer = buf
		src.connect(f)
		f.connect(g)
		g.connect(dest)
		const room = Math.max(buf.duration - total - 0.05, 0.01)
		src.start(t, Math.random() * room, total + 0.03)
	}

	/** Filtered noise that sweeps up and back down: every swish in the game. */
	private noiseArc(
		t: number,
		f0: number,
		fm: number,
		f1: number,
		q: number,
		atk: number,
		dec: number,
		peak: number,
		dest: AudioNode,
	): void {
		const ctx = this.ctx as AudioContext
		const buf = this.noiseBuf
		if (!buf) return
		const total = Math.max(atk, 0.0005) + Math.max(dec, 0.005)
		const f = ctx.createBiquadFilter()
		f.type = 'bandpass'
		f.Q.value = q
		f.frequency.setValueAtTime(clamp(f0, 20, 20000), t)
		f.frequency.exponentialRampToValueAtTime(clamp(fm, 20, 20000), t + total * 0.45)
		f.frequency.exponentialRampToValueAtTime(clamp(f1, 20, 20000), t + total)
		const g = ctx.createGain()
		this.env(g.gain, t, peak, atk, dec)
		const src = ctx.createBufferSource()
		src.buffer = buf
		src.connect(f)
		f.connect(g)
		g.connect(dest)
		const room = Math.max(buf.duration - total - 0.05, 0.01)
		src.start(t, Math.random() * room, total + 0.03)
	}

	// ── the one public trigger ───────────────────────────────────────────────

	play(name: SfxName, opts?: PlayOpts): void {
		const ctx = this.ctx
		const bus = this.sfxBus
		if (!ctx || !bus || !this.noiseBuf) return
		if (ctx.state === 'suspended') void ctx.resume().catch(() => {})

		const mix = MIX[name]
		const level = mix.gain * (opts?.gain ?? 1)
		if (level <= 0.0005) return
		const r = Math.max(opts?.rate ?? 1 + (Math.random() * 2 - 1) * mix.vary, 0.05)

		const out = ctx.createGain()
		out.gain.value = level
		const pan = this.makePanner(opts?.pan ?? 0)
		out.connect(pan)
		pan.connect(bus)

		const t = ctx.currentTime + 0.005
		try {
			this.synth(name, t, r, out)
		} catch {
			// A browser that refuses a node type does not get to take the game down.
		}
		window.setTimeout(
			() => {
				out.disconnect()
				pan.disconnect()
			},
			(mix.dur / Math.min(r, 1) + 0.3) * 1000,
		)
	}

	private synth(name: SfxName, t: number, r: number, out: GainNode): void {
		switch (name) {
			case 'tick':
				this.vTick(t, r, out)
				break
			case 'tickFinal':
				this.vTickFinal(t, r, out)
				break
			case 'whoosh':
				this.noiseArc(t, 380 * r, 2400 * r, 700 * r, 1.2, 0.14, 0.24, 0.6, out)
				break
			case 'squeak':
				this.vSqueak(t, r, out)
				break
			case 'grunt':
				this.vGrunt(t, r, out)
				break
			case 'squat':
				this.vSquat(t, r, out)
				break
			case 'hop':
				this.vHop(t, r, out)
				break
			case 'crackFlush':
				this.vCrackFlush(t, r, out)
				break
			case 'crackGood':
				this.vCrackGood(t, r, out)
				break
			case 'clank':
				this.vClank(t, r, out)
				break
			case 'donk':
				this.vDonk(t, r, out)
				break
			case 'whiffAir':
				this.noiseArc(t, 700 * r, 1600 * r, 500 * r, 0.8, 0.11, 0.24, 0.5, out)
				break
			case 'bounceTurf':
				this.vBounceTurf(t, r, out)
				break
			case 'bounceHard':
				this.vBounceHard(t, r, out)
				break
			case 'splash':
				this.vSplash(t, r, out)
				break
			case 'skim':
				this.vSkim(t, r, out)
				break
			case 'plug':
				this.vPlug(t, r, out)
				break
			case 'tantrum':
				this.vTantrum(t, r, out)
				break
			case 'stormOff':
				this.vStormOff(t, r, out)
				break
			case 'plonk':
				this.vPlonk(t, r, out)
				break
			case 'piano':
				this.vPiano(t, r, out)
				break
			case 'rest':
				this.vRest(t, r, out)
				break
			case 'sunset':
				this.vSunset(t, r, out)
				break
			case 'uiStamp':
				this.vUiStamp(t, r, out)
				break
		}
	}

	// ── the telegraph ────────────────────────────────────────────────────────

	private vTick(t: number, r: number, out: GainNode): void {
		this.tone('triangle', t, 2350 * r, 1950 * r, 0.001, 0.035, 0.7, out)
		this.noise(t, 'bandpass', 3600 * r, 3600 * r, 1.5, 0.0008, 0.012, 0.5, out)
	}

	/** The third beat. Brighter, with a body under it, so it reads as *now*. */
	private vTickFinal(t: number, r: number, out: GainNode): void {
		this.tone('triangle', t, 3100 * r, 2500 * r, 0.001, 0.06, 0.75, out)
		this.tone('sine', t, 1560 * r, 1420 * r, 0.001, 0.09, 0.35, out)
		this.noise(t, 'bandpass', 4800 * r, 4200 * r, 1.2, 0.0008, 0.02, 0.55, out)
	}

	// ── the golfer ───────────────────────────────────────────────────────────

	/** Glove re-grip: leather squeal. The caller stacks 1-5 of these as the anger tell. */
	private vSqueak(t: number, r: number, out: GainNode): void {
		this.noiseArc(t, 1000 * r, 2300 * r, 1500 * r, 20, 0.008, 0.15, 0.9, out)
		this.tone('sine', t, 1800 * r, 1400 * r, 0.01, 0.12, 0.06, out)
	}

	/** Wordless mumble. The caller raises opts.rate with anger; timing stays put. */
	private vGrunt(t: number, r: number, out: GainNode): void {
		const ctx = this.ctx as AudioContext
		const formant = ctx.createBiquadFilter()
		formant.type = 'bandpass'
		formant.frequency.value = clamp(760 * r, 120, 4000)
		formant.Q.value = 3.4
		const lp = ctx.createBiquadFilter()
		lp.type = 'lowpass'
		lp.frequency.value = clamp(2400 * r, 200, 12000)
		lp.Q.value = 0.7
		formant.connect(lp)
		lp.connect(out)
		const base = 148 * r
		const contour = [1, 1.19, 0.84]
		let i = 0
		for (const m of contour) {
			const ts = t + i * 0.11
			this.tone('square', ts, base * m, base * m * 1.11, 0.012, 0.075, 0.5 - i * 0.09, formant)
			i++
		}
	}

	// ── the ball on the tee ──────────────────────────────────────────────────

	private vSquat(t: number, r: number, out: GainNode): void {
		this.tone('sine', t, 400 * r, 170 * r, 0.012, 0.15, 0.7, out)
		this.noise(t, 'lowpass', 900 * r, 400 * r, 0.9, 0.004, 0.06, 0.15, out)
	}

	private vHop(t: number, r: number, out: GainNode): void {
		this.tone('triangle', t, 240 * r, 660 * r, 0.006, 0.13, 0.7, out)
		this.noise(t, 'highpass', 2000 * r, 2000 * r, 0.7, 0.001, 0.02, 0.2, out)
	}

	// ── contact: the loudest 30 ms in the game ───────────────────────────────

	/** Tour-pro CRACK. Bright noise transient, pitched click, a whisper of ring. */
	private vCrackFlush(t: number, r: number, out: GainNode): void {
		this.noise(t, 'bandpass', 4200 * r, 3000 * r, 0.8, 0.001, 0.03, 0.9, out)
		this.noise(t, 'highpass', 6500 * r, 6500 * r, 0.7, 0.0008, 0.012, 0.5, out)
		this.tone('triangle', t, 1250 * r, 380 * r, 0.001, 0.035, 0.5, out)
		this.tone('sine', t, 175 * r, 120 * r, 0.002, 0.09, 0.55, out)
		this.tone('sine', t, 4300 * r, 4180 * r, 0.002, 0.16, 0.09, out)
	}

	/** Solid, satisfying, a shade duller. Never as crisp as flush. */
	private vCrackGood(t: number, r: number, out: GainNode): void {
		this.noise(t, 'bandpass', 2400 * r, 1700 * r, 0.7, 0.0012, 0.038, 0.8, out)
		this.tone('triangle', t, 900 * r, 300 * r, 0.001, 0.045, 0.45, out)
		this.tone('sine', t, 160 * r, 110 * r, 0.002, 0.11, 0.6, out)
		this.tone('sine', t, 2200 * r, 2100 * r, 0.003, 0.05, 0.06, out)
	}

	/** Thin strike: no bass at all, two inharmonic partials, cheap and hollow. */
	private vClank(t: number, r: number, out: GainNode): void {
		this.noise(t, 'bandpass', 1100 * r, 900 * r, 4, 0.001, 0.03, 0.55, out)
		this.tone('sine', t, 940 * r, 918 * r, 0.001, 0.18, 0.4, out)
		this.tone('sine', t, 1487 * r, 1450 * r, 0.001, 0.14, 0.26, out)
		this.tone('triangle', t, 2210 * r, 2180 * r, 0.001, 0.07, 0.1, out)
	}

	/** The shank. Loud and WRONG: inharmonic, soft-clipped, sagging in pitch. */
	private vDonk(t: number, r: number, out: GainNode): void {
		const ctx = this.ctx as AudioContext
		const ws = ctx.createWaveShaper()
		ws.curve = softClip()
		const lp = ctx.createBiquadFilter()
		lp.type = 'lowpass'
		lp.frequency.value = clamp(2600 * r, 200, 14000)
		lp.Q.value = 0.6
		ws.connect(lp)
		lp.connect(out)
		const parts = [196, 296, 437, 611]
		let i = 0
		for (const f of parts) {
			this.tone('square', t, f * r, f * r * 0.82, 0.001, 0.42 - i * 0.07, 0.4 - i * 0.07, ws)
			i++
		}
		this.noise(t, 'bandpass', 1500 * r, 700 * r, 1.4, 0.001, 0.05, 0.5, out)
		this.tone('sine', t, 140 * r, 82 * r, 0.002, 0.18, 0.5, out)
	}

	// ── the ground ───────────────────────────────────────────────────────────

	private vBounceTurf(t: number, r: number, out: GainNode): void {
		this.noise(t, 'lowpass', 480 * r, 320 * r, 0.8, 0.002, 0.08, 0.55, out)
		this.tone('sine', t, 118 * r, 76 * r, 0.003, 0.1, 0.6, out)
	}

	private vBounceHard(t: number, r: number, out: GainNode): void {
		this.noise(t, 'highpass', 1500 * r, 1500 * r, 0.7, 0.0008, 0.035, 0.6, out)
		this.tone('triangle', t, 1500 * r, 850 * r, 0.001, 0.05, 0.45, out)
		this.tone('sine', t, 2600 * r, 2540 * r, 0.002, 0.08, 0.08, out)
	}

	private vSplash(t: number, r: number, out: GainNode): void {
		this.noise(t, 'lowpass', 2600 * r, 320 * r, 0.9, 0.005, 0.45, 0.7, out)
		this.tone('sine', t, 620 * r, 130 * r, 0.004, 0.16, 0.4, out)
		this.noise(t + 0.03, 'highpass', 2600 * r, 3400 * r, 0.7, 0.02, 0.4, 0.18, out)
	}

	/** Skipping it across the pond. Top-three delight moment, so: bright and quick. */
	private vSkim(t: number, r: number, out: GainNode): void {
		this.tone('sine', t, 900 * r, 1900 * r, 0.002, 0.05, 0.4, out)
		this.noise(t, 'bandpass', 3000 * r, 3600 * r, 2, 0.001, 0.05, 0.5, out)
		this.tone('sine', t, 420 * r, 200 * r, 0.003, 0.07, 0.25, out)
	}

	/** Plugged in the rough. Dead on arrival, no ring, no bounce. */
	private vPlug(t: number, r: number, out: GainNode): void {
		this.noise(t, 'lowpass', 320 * r, 220 * r, 0.9, 0.002, 0.1, 0.7, out)
		this.tone('sine', t, 95 * r, 62 * r, 0.003, 0.1, 0.5, out)
	}

	private vRest(t: number, r: number, out: GainNode): void {
		this.noise(t, 'lowpass', 400 * r, 300 * r, 0.8, 0.002, 0.05, 0.35, out)
		this.tone('sine', t, 150 * r, 110 * r, 0.003, 0.06, 0.3, out)
	}

	// ── reactions ────────────────────────────────────────────────────────────

	/** Club slammed into the turf three times, with a growl over it. */
	private vTantrum(t: number, r: number, out: GainNode): void {
		this.noiseArc(t, 500 * r, 1900 * r, 600 * r, 1.1, 0.09, 0.18, 0.35, out)
		const beats = [0, 0.13, 0.23]
		let i = 0
		for (const b of beats) {
			const ts = t + b
			this.tone('sine', ts, 130 * r, 60 * r, 0.002, 0.13, 0.6 - i * 0.15, out)
			this.noise(ts, 'lowpass', 700 * r, 400 * r, 0.9, 0.001, 0.06, 0.4 - i * 0.1, out)
			i++
		}
		const ctx = this.ctx as AudioContext
		const lp = ctx.createBiquadFilter()
		lp.type = 'lowpass'
		lp.frequency.value = clamp(900 * r, 150, 8000)
		lp.Q.value = 1.2
		lp.connect(out)
		this.tone('sawtooth', t + 0.05, 190 * r, 90 * r, 0.04, 0.5, 0.25, lp)
	}

	/** Walking off. A resigned falling third, then the door of the cart. */
	private vStormOff(t: number, r: number, out: GainNode): void {
		const ctx = this.ctx as AudioContext
		const lp = ctx.createBiquadFilter()
		lp.type = 'lowpass'
		lp.frequency.value = clamp(1800 * r, 200, 12000)
		lp.Q.value = 0.7
		lp.connect(out)
		const notes = [392, 330, 262]
		let i = 0
		for (const n of notes) {
			const ts = t + i * 0.19
			this.tone('triangle', ts, n * r, n * r * 0.995, 0.02, 0.3, 0.45 - i * 0.06, lp)
			this.tone('sine', ts, n * r * 0.5, n * r * 0.5, 0.025, 0.26, 0.2, lp)
			i++
		}
		this.tone('sine', t + 0.62, 90 * r, 55 * r, 0.004, 0.18, 0.4, out)
		this.noise(t + 0.62, 'lowpass', 600 * r, 300 * r, 0.8, 0.002, 0.1, 0.25, out)
	}

	// ── the moments that need silence around them ────────────────────────────

	/** Into the cup: three rattles on the rim, then the drop. Let it ring. */
	/**
	 * One soft piano note, for the hole in one and for nothing else.
	 *
	 * Struck felt rather than hammer: a quiet key-thump, a fundamental with a
	 * handful of slightly stretched partials so it reads as a string and not an
	 * organ, and a long decay under a closing lowpass. Everything else in the
	 * game is a clatter or a grunt; this is the only sound that is allowed to be
	 * beautiful, so it is deliberately the plainest thing here.
	 */
	private vPiano(t: number, r: number, out: GainNode): void {
		const ctx = this.ctx as AudioContext
		const f0 = 392 * r // G4

		const body = ctx.createBiquadFilter()
		body.type = 'lowpass'
		body.frequency.setValueAtTime(clamp(3600 * r, 200, 12000), t)
		body.frequency.exponentialRampToValueAtTime(clamp(900 * r, 120, 12000), t + 3.4)
		body.Q.value = 0.4
		body.connect(out)

		// Real strings run slightly sharp in the upper partials; the stretch is
		// what stops a stack of sines sounding like an organ.
		const partials: [number, number, number][] = [
			[1, 1.0, 3.8],
			[2, 0.44, 2.6],
			[3, 0.2, 1.9],
			[4, 0.11, 1.3],
			[5.02, 0.06, 0.9],
			[6.05, 0.035, 0.7],
		]
		for (const [mult, amp, dur] of partials) {
			this.tone('sine', t, f0 * mult, f0 * mult * 0.9985, 0.006, dur, amp * 0.5, body)
		}
		// The key going down, felt more than heard.
		this.noise(t, 'lowpass', 900 * r, 700 * r, 0.7, 0.002, 0.05, 0.05, out)
	}

	private vPlonk(t: number, r: number, out: GainNode): void {
		const ctx = this.ctx as AudioContext
		const rims = [0, 0.045, 0.085]
		let i = 0
		for (const d of rims) {
			const ts = t + d
			this.noise(ts, 'bandpass', 3200 * r, 2900 * r, 3, 0.0008, 0.015, 0.3 - i * 0.07, out)
			this.tone('triangle', ts, (1800 - i * 250) * r, (1500 - i * 250) * r, 0.001, 0.03, 0.24, out)
			i++
		}
		const cup = ctx.createBiquadFilter()
		cup.type = 'bandpass'
		cup.frequency.value = clamp(330 * r, 60, 6000)
		cup.Q.value = 6
		cup.connect(out)
		const td = t + 0.11
		this.tone('sine', td, 340 * r, 300 * r, 0.004, 0.9, 0.9, cup)
		this.tone('triangle', td, 680 * r, 640 * r, 0.004, 0.45, 0.35, cup)
		this.tone('sine', td, 110 * r, 96 * r, 0.006, 0.4, 0.3, out)
	}

	/** End of the day. A warm suspended chord that swells and lets go. */
	private vSunset(t: number, r: number, out: GainNode): void {
		const ctx = this.ctx as AudioContext
		const lp = ctx.createBiquadFilter()
		lp.type = 'lowpass'
		lp.frequency.value = clamp(1400 * r, 200, 12000)
		lp.Q.value = 0.6
		lp.connect(out)
		const chord = [196, 246.94, 293.66, 392]
		let i = 0
		for (const f of chord) {
			const detune = 1 + (i % 2 === 0 ? 0.0022 : -0.0022)
			this.tone('triangle', t + i * 0.06, f * r, f * r * detune, 0.7, 2.4, 0.16, lp)
			i++
		}
		this.tone('sine', t + 0.35, 1568 * r, 1568 * r, 0.9, 1.9, 0.04, out)
	}

	private vUiStamp(t: number, r: number, out: GainNode): void {
		this.noise(t, 'lowpass', 900 * r, 500 * r, 0.9, 0.001, 0.05, 0.7, out)
		this.tone('sine', t, 220 * r, 130 * r, 0.002, 0.08, 0.5, out)
		this.noise(t, 'highpass', 3000 * r, 3000 * r, 0.7, 0.0008, 0.01, 0.3, out)
	}

	// ── buses ────────────────────────────────────────────────────────────────

	/** 0 = full bed, 1 = the near-silence the crack lands on. */
	setDuck(amount01: number): void {
		this.duckTarget = clamp(amount01, 0, 1)
		const ctx = this.ctx
		const duck = this.duckBus
		if (!ctx || !duck) return
		const v = 1 - this.duckTarget * (1 - DUCK_FLOOR)
		duck.gain.setTargetAtTime(v, ctx.currentTime, DUCK_TAU)
	}

	/** Flight speed, normalised. Drives a filtered noise rush past the ball. */
	setWind(speed01: number): void {
		this.windTarget = clamp(speed01, 0, 1)
		const ctx = this.ctx
		if (!ctx || !this.duckBus || !this.noiseBuf) return
		this.ensureWind()
		const g = this.windGain
		const lp = this.windLp
		if (!g || !lp) return
		const s = this.windTarget
		g.gain.setTargetAtTime(s * s * WIND_MAX_GAIN, ctx.currentTime, 0.08)
		lp.frequency.setTargetAtTime(400 + 2600 * s, ctx.currentTime, 0.1)
	}

	private ensureWind(): void {
		if (this.windGain) return
		const ctx = this.ctx
		const buf = this.noiseBuf
		const duck = this.duckBus
		if (!ctx || !buf || !duck) return
		const src = ctx.createBufferSource()
		src.buffer = buf
		src.loop = true
		const hp = ctx.createBiquadFilter()
		hp.type = 'highpass'
		hp.frequency.value = 200
		hp.Q.value = 0.7
		const lp = ctx.createBiquadFilter()
		lp.type = 'lowpass'
		lp.frequency.value = 400
		lp.Q.value = 1.1
		const g = ctx.createGain()
		g.gain.value = 0
		src.connect(hp)
		hp.connect(lp)
		lp.connect(g)
		g.connect(duck)
		src.start()
		this.windGain = g
		this.windLp = lp
	}

	// ── ambience: birds, a mower two fairways over, someone else's good shot ──

	startAmbience(): void {
		this.ambWanted = true
		const ctx = this.ctx
		const amb = this.ambBus
		if (!ctx || !amb || this.ambOn) return
		this.ambOn = true
		amb.gain.cancelScheduledValues(ctx.currentTime)
		amb.gain.setValueAtTime(0.0001, ctx.currentTime)
		amb.gain.linearRampToValueAtTime(AMBIENCE_GAIN, ctx.currentTime + 1.2)
		this.startMower()
		this.birdTimer = window.setTimeout(this.loopBird, 1200 + Math.random() * 2000)
		this.thwackTimer = window.setTimeout(this.loopThwack, 6000 + Math.random() * 12000)
	}

	stopAmbience(): void {
		this.ambWanted = false
		if (!this.ambOn) return
		this.ambOn = false
		if (this.birdTimer !== null) window.clearTimeout(this.birdTimer)
		if (this.thwackTimer !== null) window.clearTimeout(this.thwackTimer)
		this.birdTimer = null
		this.thwackTimer = null
		const ctx = this.ctx
		const amb = this.ambBus
		if (!ctx || !amb) return
		const t = ctx.currentTime
		amb.gain.cancelScheduledValues(t)
		amb.gain.setValueAtTime(Math.max(amb.gain.value, 0.0001), t)
		amb.gain.exponentialRampToValueAtTime(0.0001, t + 0.5)
		for (const o of this.ambSources) o.stop(t + 0.6)
		const chain = this.ambChain
		this.ambSources = []
		this.ambChain = []
		window.setTimeout(() => {
			for (const n of chain) n.disconnect()
		}, 900)
	}

	private startMower(): void {
		const ctx = this.ctx
		const amb = this.ambBus
		if (!ctx || !amb) return
		const g = ctx.createGain()
		g.gain.value = 0.055
		const lp = ctx.createBiquadFilter()
		lp.type = 'lowpass'
		lp.frequency.value = 240
		lp.Q.value = 0.9
		const ga = ctx.createGain()
		ga.gain.value = 0.6
		const gb = ctx.createGain()
		gb.gain.value = 0.35
		const a = ctx.createOscillator()
		a.type = 'sawtooth'
		a.frequency.value = 46.5
		const b = ctx.createOscillator()
		b.type = 'sawtooth'
		b.frequency.value = 93.7
		// Very slow drift, as if it were working its way across a far fairway.
		const lfo = ctx.createOscillator()
		lfo.type = 'sine'
		lfo.frequency.value = 0.06
		const lfoG = ctx.createGain()
		lfoG.gain.value = 0.032
		a.connect(ga)
		b.connect(gb)
		ga.connect(lp)
		gb.connect(lp)
		lp.connect(g)
		g.connect(amb)
		lfo.connect(lfoG)
		lfoG.connect(g.gain)
		a.start()
		b.start()
		lfo.start()
		this.ambSources = [a, b, lfo]
		this.ambChain = [ga, gb, lp, g, lfoG]
	}

	private loopBird = (): void => {
		if (!this.ambOn) return
		this.bird()
		this.birdTimer = window.setTimeout(this.loopBird, 2400 + Math.random() * 5400)
	}

	private loopThwack = (): void => {
		if (!this.ambOn) return
		this.farThwack()
		this.thwackTimer = window.setTimeout(this.loopThwack, 13000 + Math.random() * 21000)
	}

	private bird(): void {
		const ctx = this.ctx
		const amb = this.ambBus
		if (!ctx || !amb) return
		const t = ctx.currentTime + 0.02
		const g = ctx.createGain()
		g.gain.value = 0.5 + Math.random() * 0.5
		const p = this.makePanner(Math.random() * 1.4 - 0.7)
		g.connect(p)
		p.connect(amb)
		const n = 2 + Math.floor(Math.random() * 3)
		const base = 2050 + Math.random() * 1300
		for (let i = 0; i < n; i++) {
			const ts = t + i * (0.055 + Math.random() * 0.05)
			const f = base * (1 + i * 0.04)
			this.tone('sine', ts, f, f * 1.5, 0.004, 0.045, 0.09, g)
			this.tone('sine', ts + 0.045, f * 1.5, f * 0.92, 0.004, 0.04, 0.05, g)
		}
		window.setTimeout(() => {
			g.disconnect()
			p.disconnect()
		}, 1200)
	}

	/** Somebody else, somewhere out there, catching one flush. */
	private farThwack(): void {
		const ctx = this.ctx
		const amb = this.ambBus
		if (!ctx || !amb) return
		const t = ctx.currentTime + 0.02
		const g = ctx.createGain()
		g.gain.value = 0.35 + Math.random() * 0.3
		const p = this.makePanner(Math.random() * 1.6 - 0.8)
		g.connect(p)
		p.connect(amb)
		this.noise(t, 'lowpass', 1100, 500, 0.8, 0.004, 0.06, 0.22, g)
		this.tone('sine', t, 190, 120, 0.003, 0.09, 0.16, g)
		// The trees give it back a beat later, which is what sells the distance.
		this.noise(t + 0.19, 'lowpass', 700, 400, 0.8, 0.02, 0.12, 0.06, g)
		window.setTimeout(() => {
			g.disconnect()
			p.disconnect()
		}, 1400)
	}
}
